// DotLoop API Service
import {
  DotloopProfile,
  DotloopTemplate,
  DotloopLoop,
  LoopCreationRequest,
  LoopCreationResponse,
  AuthStatus,
  ApiError
} from '../models/DotloopTypes'

class DotloopApiService {
  private baseUrl = '/api/dotloop'

  /**
   * Get headers including iframe auth token if available (for cross-site iframe contexts)
   */
  private getAuthHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    }
    
    // Include iframe auth token if available (bypasses cookie issues in iframes)
    const iframeAuthToken = localStorage.getItem('dotloop_iframe_auth_token')
    if (iframeAuthToken) {
      headers['X-Iframe-Auth-Token'] = iframeAuthToken
    }
    
    return headers
  }
  
  /**
   * Clear the iframe auth token (call this when switching accounts)
   */
  clearIframeAuthToken(): void {
    localStorage.removeItem('dotloop_iframe_auth_token')
  }

  /**
   * Check if user is authenticated with DotLoop
   */
  async checkAuthStatus(): Promise<AuthStatus> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/status`, {
        credentials: 'include',
        headers: this.getAuthHeaders()
      })
      
      if (!response.ok) {
        throw new Error('Failed to check auth status')
      }

      return await response.json()
    } catch (error) {
      console.error('Error checking auth status:', error)
      return {
        authenticated: false,
        hasRefreshToken: false,
        expiresAt: null,
        needsReauth: true,
        validationError: error instanceof Error ? error.message : 'Failed to check auth',
        dotloopEmail: null,
        dotloopAccountId: null
      }
    }
  }

  /**
   * Get the OAuth authorization URL
   * @param forceSwitch - If true, uses the switch flow that revokes current token first
   */
  getAuthUrl(forceSwitch: boolean = false, fubUserId?: number): string {
    const queryParams = new URLSearchParams()
    if (typeof fubUserId === 'number' && Number.isInteger(fubUserId) && fubUserId > 0) {
      queryParams.set('fub_user_id', String(fubUserId))
    }

    if (forceSwitch) {
      // Use the switch flow when user wants to change accounts
      // Pass current token so server can revoke it
      const currentToken = localStorage.getItem('dotloop_iframe_auth_token')
      if (currentToken) {
        queryParams.set('current_token', currentToken)
      }
      const queryString = queryParams.toString()
      return queryString ? `/dotloop/auth/switch?${queryString}` : '/dotloop/auth/switch'
    }

    const queryString = queryParams.toString()
    return queryString ? `/dotloop/auth?${queryString}` : '/dotloop/auth'
  }

  /**
   * Reconnect user by FUB user ID using persisted Dotloop tokens.
   */
  async reconnect(fubUserId: number): Promise<{
    authenticated: boolean
    iframeAuthToken?: string
    dotloopEmail?: string | null
    dotloopAccountId?: number | null
  }> {
    const response = await fetch(`${this.baseUrl}/auth/reconnect?fub_user_id=${encodeURIComponent(String(fubUserId))}`, {
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error('Failed to reconnect authentication')
    }

    return await response.json()
  }

  /**
   * Fetch all available profiles
   */
  async fetchProfiles(): Promise<DotloopProfile[]> {
    try {
      const response = await fetch(`${this.baseUrl}/profiles`, {
        credentials: 'include',
        headers: this.getAuthHeaders()
      })

      if (!response.ok) {
        const error: ApiError = await response.json()
        throw new Error(error.error || 'Failed to fetch profiles')
      }

      const data = await response.json()
      return data.data || []
    } catch (error) {
      console.error('Error fetching profiles:', error)
      throw error
    }
  }

  /**
   * Fetch loop templates for a specific profile
   */
  async fetchTemplates(profileId: number): Promise<DotloopTemplate[]> {
    try {
      const response = await fetch(`${this.baseUrl}/profiles/${profileId}/loop-templates`, {
        credentials: 'include',
        headers: this.getAuthHeaders()
      })

      if (!response.ok) {
        const error: ApiError = await response.json()
        throw new Error(error.error || 'Failed to fetch templates')
      }

      const data = await response.json()
      return data.data || []
    } catch (error) {
      console.error('Error fetching templates:', error)
      throw error
    }
  }

  /**
   * Create a new loop using the Loop-It facade
   */
  async createLoop(loopData: LoopCreationRequest): Promise<LoopCreationResponse> {
    try {
      // DEBUG: Log what we're sending to the server
      console.log('🚀 dotloopApi.createLoop - Sending to server:', {
        hasProperty: !!(loopData as any)?.property,
        propertyKeys: (loopData as any)?.property ? Object.keys((loopData as any).property).filter(k => ((loopData as any).property[k] !== undefined)) : [],
        hasParticipants: !!((loopData as any)?.participants?.length),
        participantCount: (loopData as any)?.participants?.length || 0,
        fullLoopData: loopData
      })

      const response = await fetch(`${this.baseUrl}/loops`, {
        method: 'POST',
        credentials: 'include',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(loopData)
      })

      console.log('📥 dotloopApi.createLoop - Response status:', response.status, response.ok ? '✅' : '❌')

      if (!response.ok) {
        const error: ApiError = await response.json()
        console.error('Loop creation failed:', error)
        throw new Error(error.error || 'Failed to create loop')
      }

      const result = await response.json()
      console.log('Loop created successfully:', result)
      
      return result
    } catch (error) {
      console.error('Error creating loop:', error)
      throw error
    }
  }

  /**
   * Fetch loops for a specific profile
   */
  async fetchLoops(profileId: number, filters?: {
    batch_size?: number
    batch_number?: number
    status?: string
    updated_min?: string
  }): Promise<DotloopLoop[]> {
    try {
      const queryParams = new URLSearchParams()
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined) {
            queryParams.append(key, String(value))
          }
        })
      }

      const url = `${this.baseUrl}/profiles/${profileId}/loops${queryParams.toString() ? '?' + queryParams.toString() : ''}`
      
      const response = await fetch(url, {
        credentials: 'include',
        headers: this.getAuthHeaders()
      })

      if (!response.ok) {
        const error: ApiError = await response.json()
        throw new Error(error.error || 'Failed to fetch loops')
      }

      const data = await response.json()
      return data.data || []
    } catch (error) {
      console.error('Error fetching loops:', error)
      throw error
    }
  }

  /**
   * Get account details
   */
  async fetchAccount(): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/account`, {
        credentials: 'include',
        headers: this.getAuthHeaders()
      })

      if (!response.ok) {
        const error: ApiError = await response.json()
        throw new Error(error.error || 'Failed to fetch account')
      }

      return await response.json()
    } catch (error) {
      console.error('Error fetching account:', error)
      throw error
    }
  }
}

export default new DotloopApiService()

